import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { X, QrCode, Upload, Camera } from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";

// Scan a WeChat QR code with the camera, or decode one from a photo.
const QrScanModal = ({ onClose, onScan }) => {
  const [cameraError, setCameraError] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [readerVisible, setReaderVisible] = useState(false);
  const fileRef = useRef();
  const doneRef = useRef(false);
  const scannerRef = useRef(null);

  const stopCamera = () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setCameraOn(false);
    setReaderVisible(false);
    if (s) {
      try {
        const video = document.querySelector('#wechat-qr-reader video');
        if (video && video.srcObject) video.srcObject.getTracks().forEach(t => t.stop());
      } catch {}
      const state = s.getState();
      if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
        s.stop().then(() => { try { s.clear(); } catch {} }).catch(() => {});
      }
    }
  };

  const startCamera = async () => {
    setCameraError(false);
    setReaderVisible(true); // the reader element must be visible before the scanner starts
    await new Promise(r => {
      let frames = 0;
      const check = () => {
        const el = document.getElementById("wechat-qr-reader");
        if ((el && el.offsetWidth > 0) || ++frames > 60) return r();
        requestAnimationFrame(check);
      };
      check();
    });
    const scanner = new Html5Qrcode("wechat-qr-reader");
    scannerRef.current = scanner;
    try {
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (text) => {
          if (doneRef.current) return;
          doneRef.current = true;
          stopCamera();
          onScan(text);
        },
        () => {}
      );
      setCameraOn(true);
    } catch (err) {
      console.error("QR camera start failed:", err);
      scannerRef.current = null;
      setReaderVisible(false);
      setCameraError(true);
      toast.error("Camera could not start — allow camera permission, or upload a QR photo instead");
    }
  };

  // Stop the camera if the modal closes
  useEffect(() => () => {
    const s = scannerRef.current;
    if (s) {
      try {
        const video = document.querySelector('#wechat-qr-reader video');
        if (video && video.srcObject) video.srcObject.getTracks().forEach(t => t.stop());
      } catch {}
      const state = s.getState();
      if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
        s.stop().then(() => { try { s.clear(); } catch {} }).catch(() => {});
      }
    }
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const scanner = new Html5Qrcode("wechat-qr-file");
      const text = await scanner.scanFile(file, true);
      if (!doneRef.current) {
        doneRef.current = true;
        onScan(text);
      }
    } catch {
      toast.error("No QR code found in that photo — try a clearer one");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="qr-scan-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card rounded-sm p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif font-semibold text-lg flex items-center gap-2">
            <QrCode size={18} className="trade-gold" /> Scan WeChat QR
          </h3>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white transition-colors" data-testid="qr-scan-close">
            <X size={18} />
          </button>
        </div>

        <div
          id="wechat-qr-reader"
          className={`rounded-sm overflow-hidden bg-black min-h-[200px] ${readerVisible ? "" : "hidden"}`}
          data-testid="qr-camera-view"
        />
        {!readerVisible && (
          <div className="border border-dashed border-white/15 rounded-sm p-8 text-center" data-testid="qr-camera-idle">
            <QrCode className="w-10 h-10 mx-auto mb-3 trade-gold opacity-60" />
            <p className="text-white/50 text-sm mb-4">
              {cameraError ? "Camera not available — upload a QR photo instead" : "Point the camera at their WeChat QR code"}
            </p>
            {!cameraError && (
              <Button onClick={startCamera} className="trade-btn-primary rounded-sm h-11 px-6" data-testid="qr-start-camera-btn">
                <Camera size={16} className="mr-2" /> Start Camera
              </Button>
            )}
          </div>
        )}
        {/* hidden element for file decoding */}
        <div id="wechat-qr-file" className="hidden" />

        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="qr-scan-file-input" />
        <Button
          onClick={() => fileRef.current?.click()}
          className="w-full mt-4 bg-white/10 text-white hover:bg-white/20 rounded-sm h-11"
          data-testid="qr-upload-photo-btn"
        >
          <Upload size={16} className="mr-2" /> Or upload a QR photo
        </Button>
        <p className="text-[11px] text-white/40 mt-3 text-center">
          Point the camera at their WeChat QR code (WeChat → Me → profile → My QR Code)
        </p>
      </motion.div>
    </div>
  );
};

export default QrScanModal;
